import SparkMD5 from "spark-md5";
import {
  getAgentRegisterConfig,
  getVerificationEmailTemplateId,
  shouldCallAgentRegisterApis,
} from "../../../config/agentRegisterConfig";
import {
  relayFetch,
  relayFetchJson,
  relayFetchText,
} from "../../../services/relayService";

const DRY_RUN_AGENCY = {
  organizationId: "dry-run-organization-id",
  agencyId: "dry-run-agency-id",
  intair: "dry-run-intair",
};

const DRY_RUN_TTS_ID = "00000";

function logDryRun(step, payload) {
  console.log(`[AgentRegister dry-run] ${step}`, payload);
}

const upper = (value) => value.toUpperCase();
const langCode = (language) => language.toLowerCase().slice(0, 2);
const newsletterLang = (language) => upper(langCode(language));

function randomString(length) {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function buildTtsXml(data, password) {
  const config = getAgentRegisterConfig();
  const langLetter = upper(langCode(data.language).charAt(0));
  const birthDateTts = data.birthDate.replace(/-/g, "");

  return `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/">
  <soap:Body>
    <RequestXML>
      <SOURCE>
        <TCNA_USERNAME>${config.ttsUsername}</TCNA_USERNAME>
        <TCNA_PASSWORD>${config.ttsPassword}</TCNA_PASSWORD>
        <REQUEST>${config.ttsRequest}</REQUEST>
      </SOURCE>
      <REQUESTDETAILS>
        <ACTION>ADD</ACTION>
        <AGENCYNUMBER>${upper(data.agencyPhone)}</AGENCYNUMBER>
        <AGENCYPASSWORD>${upper(data.agencyPin)}</AGENCYPASSWORD>
        <AGENTNAME>${upper(data.userName)}</AGENTNAME>
        <USERNAME>${upper(data.userName)}</USERNAME>
        <PASSWORD>${password}</PASSWORD>
        <EMAIL>${upper(data.email)}</EMAIL>
        <FIRSTNAME>${upper(data.firstName)}</FIRSTNAME>
        <LASTNAME>${upper(data.lastName)}</LASTNAME>
        <GENDER>${upper(data.gender)}</GENDER>
        <TELEPHONE>${data.agencyPhone || ""}</TELEPHONE>
        <TELEPHONE_EXT/>
        <MOBILE>${data.phone || ""}</MOBILE>
        <COMMUNICATIONS>${langLetter}</COMMUNICATIONS>
        <LANGUAGE>${langLetter}</LANGUAGE>
        <RIU_RPC/>
        <BIRTHDATE>${birthDateTts}</BIRTHDATE>
        <AGENT_ADDRESS1/>
        <AGENT_ADDRESS2/>
        <AGENT_CITY/>
        <AGENT_STATE/>
        <AGENT_POSTAL/>
        <AGENT_COUNTRY/>
        <EMAIL_ALTERNATE/>
        <TELEPHONE_ALTERNATE/>
      </REQUESTDETAILS>
    </RequestXML>
  </soap:Body>
</soap:Envelope>`;
}

function parseTtsId(xmlText) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "text/xml");

  const errorsNode = doc.querySelector("ERRORS");
  if (errorsNode) {
    const errorMsg = errorsNode.querySelector("ERROR_MSG")?.textContent;
    throw new Error(errorMsg || "TTS agent creation failed.");
  }

  const statusMessage = doc.querySelector("STATUS_MESSAGE")?.textContent;

  if (!statusMessage) {
    throw new Error("Unable to parse TTS response.");
  }

  const idMatch = statusMessage.match(/TTS_(?:AGENTNO|ID)=(\d+)/);
  if (!idMatch) {
    throw new Error(statusMessage);
  }

  return idMatch[1];
}

async function getAgencyByPhone(agencyPhone) {
  const { accessApiUrl } = getAgentRegisterConfig();
  const forwardUrl = `${accessApiUrl}/agency/getAgencybyPhone?ttsId=${agencyPhone}`;

  if (!shouldCallAgentRegisterApis()) {
    logDryRun("Step 1: validate agency by phone", { method: "GET", forwardUrl });
    return {
      Status: "A",
      OrganizationId: DRY_RUN_AGENCY.organizationId,
      AgencyId: DRY_RUN_AGENCY.agencyId,
      Intair: DRY_RUN_AGENCY.intair,
      AccountingInfo: { managerPassword: "DRY-RUN-PIN" },
    };
  }

  const { response, data } = await relayFetchJson(forwardUrl);

  if (!response.ok || !data?.Items?.length) {
    throw new Error("Invalid Agency Phone");
  }

  const agency = data.Items[0];

  if (agency.Status !== "A") {
    throw new Error("Invalid Agency Phone");
  }

  return agency;
}

function toAgencyRecord(agency) {
  return {
    organizationId: agency.OrganizationId,
    agencyId: agency.AgencyId,
    intair: agency.Intair,
  };
}

export async function validateAgencyByPhone(agencyPhone) {
  const agency = await getAgencyByPhone(agencyPhone);
  return toAgencyRecord(agency);
}

export async function validateAgency(agencyPhone, agencyPin) {
  if (!shouldCallAgentRegisterApis()) {
    logDryRun("Step 1: validate agency pin", {
      agencyPhone,
      agencyPin: upper(agencyPin),
    });
    return { ...DRY_RUN_AGENCY };
  }

  const agency = await getAgencyByPhone(agencyPhone);

  const managerPassword = agency.AccountingInfo?.managerPassword;
  if (!managerPassword) {
    throw new Error("Invalid Agency Phone");
  }

  if (upper(managerPassword) !== upper(agencyPin)) {
    throw new Error("Wrong Agency Password");
  }

  return toAgencyRecord(agency);
}

export async function createTtsAgent(formData, password) {
  const { ttsWebserviceUrl, ttsTimeoutMs } = getAgentRegisterConfig();
  const xml = buildTtsXml(formData, password)
    .replace(/\s+/g, " ")
    .replace(/>\s+</g, "><")
    .trim();
  const encodedXml = encodeURIComponent(encodeURIComponent(xml));
  const forwardUrl = `${ttsWebserviceUrl}?RequestXML=${encodedXml}`;

  if (!shouldCallAgentRegisterApis()) {
    logDryRun("Step 3: create TTS agent", { method: "GET", forwardUrl, xml });
    return DRY_RUN_TTS_ID;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), ttsTimeoutMs);

  try {
    const { text } = await relayFetchText(forwardUrl, {
      method: "GET",
      signal: controller.signal,
      headers: { "Content-Type": "text/xml; charset=utf-8" },
    });
    return parseTtsId(text);
  } finally {
    clearTimeout(timeout);
  }
}

export async function createAgentProfile(formData, agency, ttsId, password) {
  const { accessApiUrl } = getAgentRegisterConfig();
  const lang = langCode(formData.language);
  const newsletterSubscribed =
    formData.newsLetterSubscription === "subscribe" ||
    formData.newsLetterSubscription === "1";

  const payload = {
    organizationId: agency.organizationId,
    ttsId,
    address1: "",
    address2: "",
    postalCode: "",
    password,
    currency: " ",
    newsletterLanguage: newsletterLang(formData.language),
    lang,
    gender: upper(formData.gender),
    birthdate: formData.birthDate,
    isManager: null,
    status: true,
    isIcVal: null,
    newsletterSubscription: newsletterSubscribed,
    isPrefrencesUpdated: null,
    intrestedProducts: formData.interests.join(","),
    allowPushNotifications: upper(formData.allowPushNotifications),
    bookingNotificationLanguage: newsletterLang(
      formData.bookingNotificationLanguage
    ),
    names: {
      first: upper(formData.firstName),
      last: upper(formData.lastName),
      username: upper(formData.userName),
    },
    roles: {
      manager: 0,
      eDocMaster: 0,
      admin: 0,
    },
    agency: {
      id: agency.agencyId,
      phone: formData.agencyPhone,
      intair: agency.intair,
    },
    contactInfo: {
      phone: formData.phone
        ? [
            {
              number: formData.phone,
              type: "mobile",
              status: "notVerified",
            },
          ]
        : [{ number: "", type: "", status: "" }],
      email: [
        {
          email: upper(formData.email),
          type: "primary",
          status: "notVerified",
        },
      ],
    },
  };

  const forwardUrl = `${accessApiUrl}/agent`;

  if (!shouldCallAgentRegisterApis()) {
    logDryRun("Step 4: create agent profile", {
      method: "POST",
      forwardUrl,
      payload,
    });
    return { agentId: "dry-run-agent-id", ...payload };
  }

  const { response, data } = await relayFetchJson(forwardUrl, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(data?.message || "Something went wrong");
  }

  return data;
}

export async function createSsoAudit(formData, agency, ttsId, password) {
  const { accessApiUrl } = getAgentRegisterConfig();
  const payload = {
    action: "CHANGE",
    status: "",
    password: SparkMD5.hash(password),
    ipExternal: "",
    agency: formData.agencyPhone,
    agencyIntair: agency.intair,
    agentTTSId: ttsId,
    unlockedBy: "",
  };

  if (!shouldCallAgentRegisterApis()) {
    logDryRun("Step 5: SSO audit", {
      method: "POST",
      forwardUrl: `${accessApiUrl}/ssoaudit`,
      payload,
    });
    return;
  }

  try {
    await relayFetch(`${accessApiUrl}/ssoaudit`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  } catch {
    // SSO sync failure must not block registration.
  }
}

export async function sendVerificationEmail(formData, ttsId) {
  const { notificationApiUrl, verificationTokenLength } =
    getAgentRegisterConfig();
  const lang = langCode(formData.language);
  const random = randomString(verificationTokenLength);
  const tokenPayload = `agentttsId=${ttsId}&random=${random}&page=register`;
  const templateId = getVerificationEmailTemplateId(lang);

  const payload = {
    name: `${upper(formData.firstName)} ${upper(formData.lastName)}`,
    token: tokenPayload,
    email: upper(formData.email),
    templateId,
    language: lang,
  };

  if (!shouldCallAgentRegisterApis()) {
    logDryRun("Step 6: send verification email", {
      method: "POST",
      forwardUrl: `${notificationApiUrl}/sendVerificationEmail`,
      payload,
    });
    return;
  }

  try {
    const forwardUrl = `${notificationApiUrl}/sendVerificationEmail`;
    const { response } = await relayFetchJson(forwardUrl, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error("Something went wrong");
    }
  } catch {
    // Agent is registered even if email fails.
  }
}

export function resolveAgentPassword(password) {
  const { tempRegisterPassword } = getAgentRegisterConfig();
  return upper(password.trim() || tempRegisterPassword);
}

export async function registerAgent(formData) {
  const password = resolveAgentPassword(formData.agentPassword);

  const agency = await validateAgency(formData.agencyPhone, formData.agencyPin);
  const ttsId = await createTtsAgent(formData, password);
  await createAgentProfile(formData, agency, ttsId, password);
  await createSsoAudit(formData, agency, ttsId, password);
  // await sendVerificationEmail(formData, ttsId);
}

export function buildFormDataFromState(state) {
  const config = getAgentRegisterConfig();
  const lang = langCode(state.language);

  return {
    agencyPhone: state.agencyPhone.replace(/\D/g, ""),
    agencyPin: upper(state.agencyPin),
    userName: upper(state.userName),
    firstName: upper(state.firstName),
    lastName: upper(state.lastName),
    email: upper(state.email),
    phone: state.phone.replace(/\D/g, ""),
    gender: upper(state.gender),
    birthDate: state.birthDate,
    language: lang,
    agentPassword: state.agentPassword,
    newsLetterSubscription: config.newsletterSubscription,
    interests: config.defaultInterests,
    allowPushNotifications: config.allowPushNotifications,
    bookingNotificationLanguage: lang,
  };
}

export { getDefaultRegistrationLanguage } from "../../../config/agentRegisterConfig";
